import React from 'react';
import { Box, Button, Dialog, Divider, Grid, Skeleton, styled, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';

import { useCurrencyString } from 'lib/useCurrencyString';
import logger from 'lib/logger';
import { useDojoCard } from './useDojoCard';
import { useDojoWallet } from './useDojoWallet';
import { useDojoSdk } from './useDojoSdk';
import { LoadingButton } from 'ui/LoadingButton';
import { orderApi } from 'components/order/orderApi';
import { IPaymentIntentResponse, OrderPaymentType } from 'components/order/model/Order';
import { PaymentProviderEnum } from '@pepperhq/types-sdk';
import { useAuth } from 'lib/useAuth';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import { ThreeDsGlobalStyles } from './ThreeDsGlobalStyles';
import { isApplePayReady } from '../ApplePayButton';
import { isDefined } from 'lib/typeInference';
import { useTranslation } from 'react-i18next';

const DOJO_UNKNOWN_ERROR_KEY = 'DIALOG_DOJO_UNKNOWN_ERROR_DESCRIPTION';

const dojoStatusCodeErrorKeys: Record<number, string> = {
    5: 'DIALOG_DOJO_DECLINED_PAYMENT_DESCRIPTION',
    20: 'DIALOG_DOJO_DUPLICATE_PAYMENT_DESCRIPTION',
    30: 'DIALOG_DOJO_FAILED_PAYMENT_DESCRIPTION'
};

const getDojoStatusCodeErrorKey = (statusCode?: number) =>
    (isDefined(statusCode) ? dojoStatusCodeErrorKeys[statusCode] : undefined) ?? DOJO_UNKNOWN_ERROR_KEY;

const PaymentColumn = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    paddingTop: theme.spacing(8),
    width: '100%',
    maxWidth: REDUCED_CONTENT_MAX_WIDTH,
    marginInline: 'auto'
}));

const DojoCardContainer = styled('div')({
    '& iframe': {
        display: 'block',
        width: '100%'
    }
});

const DojoWalletContainer = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    // The container has to stay mounted for Dojo to construct into, so it is
    // hidden rather than unmounted when there is no wallet to show. `display`
    // above would otherwise win over the attribute's default styling.
    '&[hidden]': {
        display: 'none'
    },
    '& iframe': {
        display: 'block'
    }
});

const FieldSkeleton = styled(Skeleton)(({ theme }) => ({
    height: theme.spacing(5)
}));

const DojoCardFormSkeleton = () => (
    <>
        <FieldSkeleton variant="rounded" />
        <FieldSkeleton variant="rounded" />
        <Grid container spacing={2}>
            <Grid size={6}>
                <FieldSkeleton variant="rounded" />
            </Grid>
            <Grid size={6}>
                <FieldSkeleton variant="rounded" />
            </Grid>
        </Grid>
        <FieldSkeleton variant="rounded" />
        <Divider />
        <FieldSkeleton variant="rounded" />
    </>
);

const DojoFailedDialogContent = ({
    errorTranslationKey,
    onRetry,
    onCancel,
    onBack
}: {
    errorTranslationKey: string | undefined;
    onRetry: () => void;
    onCancel: () => void;
    onBack: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <MuiAppBar title={t('DIALOG_DOJO_FAILED_PAYMENT_TITLE')} onBack={onBack} icon={Close} ignoreGA />

            <PaymentColumn>
                <Box display="flex" flexDirection="column" gap={2} height="100%">
                    <Typography variant="body1" textAlign="center">
                        {t(errorTranslationKey ?? DOJO_UNKNOWN_ERROR_KEY)}
                    </Typography>
                    <Box sx={{ mt: 'auto' }} display="flex" gap={2} flexDirection="column">
                        <Button variant="contained" onClick={onRetry} fullWidth>
                            {t('RETRY')}
                        </Button>
                        <Button variant="outlined" onClick={onCancel} fullWidth>
                            {t('GENERAL_CANCEL')}
                        </Button>
                    </Box>
                </Box>
            </PaymentColumn>
        </>
    );
};

const TotalAmountContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
}));

const DOJO_CARD_CONTAINER_ID = 'dojo-card-container';
const DOJO_WALLET_CONTAINER_ID = 'dojo-wallet-container';

const DojoPaymentsDialogContent = ({
    totalAmount,
    locationId,
    tableNumber,
    orderId,
    onPaymentSuccess,
    onPaymentError,
    onClose
}: {
    totalAmount: number;
    locationId?: string;
    tableNumber?: string;
    orderId?: string;
    onClose: () => void;
    onPaymentError: (errorTranslationKey: string) => void;
    onPaymentSuccess: (paymentIntentId: string) => void;
}) => {
    const getCurrencyString = useCurrencyString();
    const { t } = useTranslation();
    const { user } = useAuth();

    const [loadingSessionSecret, setLoadingSessionSecret] = React.useState<boolean>(true);
    const [paymentIntent, setPaymentIntent] = React.useState<IPaymentIntentResponse['dojo'] | undefined>(
        undefined
    );
    const [executingPayment, setExecutingPayment] = React.useState<boolean>(false);

    const onWalletPaymentComplete = React.useCallback(
        (response: Dojo.PaymentResponse) => {
            if (response?.statusCode !== 0) {
                logger.error('Dojo ::: Failed to execute wallet payment', response?.message);
                onPaymentError(getDojoStatusCodeErrorKey(response?.statusCode));
                return;
            }
            if (!paymentIntent?.id) {
                return;
            }
            onPaymentSuccess(paymentIntent.id);
        },
        [paymentIntent?.id, onPaymentSuccess, onPaymentError]
    );

    const onWalletPaymentInitiated = React.useCallback(() => {
        setExecutingPayment(true);
    }, []);
    const onWalletPaymentCancelled = React.useCallback(() => {
        setExecutingPayment(false);
    }, []);

    const { isLoaded: isDojoSdkLoaded, hasFailed: dojoSdkLoadFailed } = useDojoSdk();

    // The forms below reach for `window.Dojo` as soon as they get a secret, so
    // withhold it until the SDK has landed. It downloads alongside the payment
    // intent request, so this rarely holds anything up.
    const clientSessionSecret = isDojoSdkLoaded ? paymentIntent?.clientSessionSecret : undefined;

    const {
        card: dojoCard,
        formLoadState: dojoCardFormLoadState,
        formComplete: dojoCardFormComplete
    } = useDojoCard(clientSessionSecret, DOJO_CARD_CONTAINER_ID);

    const { wallet: dojoWallet } = useDojoWallet(
        clientSessionSecret,
        DOJO_WALLET_CONTAINER_ID,
        onWalletPaymentComplete,
        onWalletPaymentInitiated,
        onWalletPaymentCancelled
    );

    React.useEffect(() => {
        if (dojoSdkLoadFailed) {
            onPaymentError(DOJO_UNKNOWN_ERROR_KEY);
        }
    }, [dojoSdkLoadFailed, onPaymentError]);

    React.useEffect(() => {
        setLoadingSessionSecret(true);
        const fetchSessionSecret = async () => {
            try {
                const userId = user ? user._id : '';
                const response = await orderApi.createPaymentIntent({
                    type: OrderPaymentType.CARD,
                    amount: totalAmount,
                    locationId,
                    provider: PaymentProviderEnum.DOJO,
                    userId,
                    tableNumber,
                    orderId: Number(orderId) || undefined
                });
                if (response.dojo?.clientSessionSecret) {
                    setPaymentIntent(response.dojo);
                }
            } catch (error) {
                logger.error('Dojo ::: Failed to create payment intent', error);
                onPaymentError(DOJO_UNKNOWN_ERROR_KEY);
            } finally {
                setLoadingSessionSecret(false);
            }
        };
        fetchSessionSecret();
    }, [locationId, totalAmount, user, onPaymentError, tableNumber, orderId]);

    const handleCardPay = React.useCallback(async () => {
        if (!dojoCard) {
            return;
        }
        setExecutingPayment(true);
        try {
            const response = await dojoCard?.executePayment();

            if (response?.statusCode !== 0) {
                logger.error('Dojo ::: Failed to execute card payment', response);
                setExecutingPayment(false);
                onPaymentError(getDojoStatusCodeErrorKey(response?.statusCode));
                return;
            }
            if (!paymentIntent?.id) {
                logger.error('Dojo ::: No payment intent id found in payment response');
                return;
            }
            onPaymentSuccess(paymentIntent.id);
        } catch (error) {
            logger.error('Dojo ::: Failed to execute card payment', error);
        } finally {
            setExecutingPayment(false);
        }
    }, [dojoCard, onPaymentSuccess, paymentIntent?.id, onPaymentError]);

    const isLoading = loadingSessionSecret || dojoCardFormLoadState === 'loading';

    const canMakeWalletPayment = React.useMemo(() => {
        if (!isDefined(dojoWallet)) {
            return false;
        }
        if ('ApplePaySession' in window) {
            return isApplePayReady();
        }
        return true;
    }, [dojoWallet]);

    return (
        <>
            <MuiAppBar title={t('DIALOG_DOJO_TITLE')} onBack={onClose} icon={Close} ignoreGA />
            <PaymentColumn>
                <TotalAmountContainer>
                    <Typography variant="h2" fontWeight="bold">
                        {getCurrencyString(totalAmount)}
                    </Typography>
                </TotalAmountContainer>
                {isLoading && <DojoCardFormSkeleton />}
                {!loadingSessionSecret && (
                    <>
                        <DojoCardContainer id={DOJO_CARD_CONTAINER_ID} />
                        <Box display="flex" flexDirection="column" gap={2}>
                            {dojoCardFormLoadState !== 'loading' && (
                                <>
                                    <LoadingButton
                                        type="submit"
                                        disabled={!dojoCardFormComplete || executingPayment}
                                        loading={executingPayment}
                                        onClick={handleCardPay}
                                        variant="contained"
                                        fullWidth
                                    >
                                        {t('DIALOG_DOJO_CARD_PAY_BUTTON')}
                                    </LoadingButton>
                                    {canMakeWalletPayment && (
                                        <Typography variant="body2" align="center" color="text.secondary">
                                            {t('DIALOG_DOJO_WALLET_PAY_DESCRIPTION')}
                                        </Typography>
                                    )}
                                </>
                            )}
                            <DojoWalletContainer
                                id={DOJO_WALLET_CONTAINER_ID}
                                hidden={!canMakeWalletPayment}
                            />
                        </Box>
                    </>
                )}
            </PaymentColumn>
        </>
    );
};

type PaymentState = 'default' | 'error';

export function DojoPaymentsDialog({
    open,
    locationId,
    totalAmount,
    tableNumber,
    orderId,
    onPaymentSuccess,
    onClose
}: {
    open: boolean;
    locationId?: string;
    totalAmount: number;
    tableNumber?: string;
    orderId?: string;
    onPaymentSuccess: (paymentIntentId: string) => void;
    onClose: () => void;
}) {
    const [paymentState, setPaymentState] = React.useState<PaymentState>('default');
    const [errorTranslationKey, setErrorTranslationKey] = React.useState<string | undefined>(undefined);

    const handlePaymentError = React.useCallback((translationKey: string) => {
        setPaymentState('error');
        setErrorTranslationKey(translationKey);
    }, []);
    const handleRetry = React.useCallback(() => {
        setPaymentState('default');
        setErrorTranslationKey(undefined);
    }, []);
    return (
        <>
            <ThreeDsGlobalStyles />
            <Dialog open={open} onClose={onClose} fullScreen>
                {paymentState === 'default' && (
                    <DojoPaymentsDialogContent
                        totalAmount={totalAmount}
                        locationId={locationId}
                        tableNumber={tableNumber}
                        orderId={orderId}
                        onPaymentSuccess={onPaymentSuccess}
                        onPaymentError={handlePaymentError}
                        onClose={onClose}
                    />
                )}
                {paymentState === 'error' && (
                    <DojoFailedDialogContent
                        errorTranslationKey={errorTranslationKey}
                        onRetry={handleRetry}
                        onCancel={onClose}
                        onBack={onClose}
                    />
                )}
            </Dialog>
        </>
    );
}
